/**
 * Normalizes captured service data into reporter-consumable shapes.
 * Maps Appium command steps and assertion steps to core ActionStep format.
 */

import type {
  ActionStep,
  ActionCategory,
  ConsoleLogEntry,
  ConsoleLogLevel,
  CapturedNetworkRequest,
  DeviceInfo,
  LogSource,
  StepKind,
} from '@testrelic/core';
import type { AppiumCommandStep, AppiumAssertionStep, AppiumNetworkEntry, DeviceLogEntry } from './types.js';

/**
 * The four-bucket category the platform's timeline and Ask AI tools filter on.
 *
 * `navigation` is its own bucket there, so it must not be folded into
 * `ui_action` the way the other interaction-ish categories are.
 */
function mapCommandCategory(category: string): ActionCategory {
  if (category === 'navigation') return 'navigation';
  if (category === 'find' || category === 'interaction' || category === 'gesture' ||
      category === 'lifecycle' || category === 'context' ||
      category === 'device' || category === 'keyboard' || category === 'alert' ||
      category === 'screenshot') {
    return 'ui_action';
  }
  return 'custom_step';
}

/**
 * Appium's own eleven categories, kept alongside the four-bucket one.
 *
 * Collapsing them meant a swipe, an app launch, a keypress and a screenshot
 * all rendered as the same "action" badge — the report could not tell a
 * gesture from a lifecycle event even though the tracker always knew.
 */
const STEP_KINDS: ReadonlySet<string> = new Set<StepKind>([
  'find', 'interaction', 'gesture', 'navigation', 'lifecycle', 'context',
  'device', 'keyboard', 'alert', 'screenshot', 'assertion', 'media',
  'script', 'flow_control', 'ai', 'other',
]);

function toStepKind(category: string): StepKind {
  return STEP_KINDS.has(category) ? (category as StepKind) : 'other';
}

/** Appium's log types, mapped onto the shared `LogSource` vocabulary. */
const LOG_SOURCES: ReadonlySet<string> = new Set<LogSource>([
  'console', 'logcat', 'syslog', 'crashlog', 'browser',
  'safariConsole', 'performance', 'server', 'bidi', 'driver',
]);

function toLogSource(source: string): LogSource {
  return LOG_SOURCES.has(source) ? (source as LogSource) : 'driver';
}

export function commandStepToAction(step: AppiumCommandStep): ActionStep {
  const title = step.selector
    ? `${step.command}(${step.selector})`
    : step.command;

  return {
    title,
    category: mapCommandCategory(step.category),
    kind: toStepKind(step.category),
    selector: step.selector ?? null,
    timestamp: step.timestamp,
    duration: step.duration,
    videoOffset: null,
    status: step.result === 'success' ? 'passed' : 'failed',
    error: step.error ?? null,
    children: [],
  };
}

export function assertionStepToAction(step: AppiumAssertionStep): ActionStep {
  const title = step.expectedValue !== undefined
    ? `expect.${step.matcherName}(${JSON.stringify(step.expectedValue)})`
    : `expect.${step.matcherName}()`;

  return {
    title,
    category: 'assertion',
    kind: 'assertion',
    timestamp: step.timestamp,
    duration: step.duration,
    videoOffset: null,
    status: step.passed ? 'passed' : 'failed',
    error: step.passed ? null : step.message,
    children: [],
  };
}

export function deviceLogToConsoleEntry(entry: DeviceLogEntry): ConsoleLogEntry {
  const levelMap: Record<string, ConsoleLogLevel> = {
    verbose: 'debug',
    debug: 'debug',
    info: 'info',
    warn: 'warn',
    error: 'error',
    fatal: 'error',
  };

  return {
    level: levelMap[entry.level] ?? 'info',
    // The tag used to be inlined into the text because there was nowhere else
    // to put it. It has its own column now, so the message stays the message.
    text: entry.message,
    timestamp: entry.timestamp,
    location: null,
    source: toLogSource(entry.source),
    tag: entry.tag ?? null,
    pid: entry.pid ?? null,
    stackTrace: entry.stackTrace ?? null,
  };
}

/** The device a run drove, from the capabilities the detector resolved. */
export function capabilitiesToDevice(caps: {
  platform: string;
  platformVersion: string | null;
  deviceName: string | null;
  automationName: string | null;
  appPath: string | null;
  browserName: string | null;
  udid: string | null;
  testMode: string;
}): DeviceInfo {
  const platform =
    caps.platform === 'android' || caps.platform === 'ios' || caps.platform === 'web'
      ? caps.platform
      : 'unknown';
  const mode =
    caps.testMode === 'native' || caps.testMode === 'hybrid' || caps.testMode === 'mobile-web'
      ? caps.testMode
      : null;
  return {
    platform,
    platformVersion: caps.platformVersion,
    deviceName: caps.deviceName,
    automationName: caps.automationName,
    appId: caps.appPath ?? caps.browserName,
    appTestMode: mode,
    udid: caps.udid,
  };
}

/** Best-effort resource type from the response MIME type. */
function resourceTypeOf(mime: string | null | undefined): CapturedNetworkRequest['resourceType'] {
  if (!mime) return 'xhr';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('font/') || mime.includes('font')) return 'font';
  if (mime.includes('javascript')) return 'script';
  if (mime.includes('css')) return 'stylesheet';
  if (mime.includes('html')) return 'document';
  if (mime.includes('json') || mime.includes('xml')) return 'xhr';
  return 'other';
}

export function networkEntryToRequest(entry: AppiumNetworkEntry): CapturedNetworkRequest {
  return {
    url: entry.url,
    method: entry.method,
    resourceType: resourceTypeOf(entry.mimeType),
    statusCode: entry.status ?? 0,
    responseTimeMs: entry.duration ?? 0,
    startedAt: entry.timestamp,
    requestHeaders: entry.requestHeaders ?? null,
    requestBody: entry.requestBody ?? null,
    responseBody: entry.responseBody ?? null,
    responseHeaders: entry.responseHeaders ?? null,
    contentType: entry.mimeType ?? null,
    responseSize: entry.size ?? 0,
    requestBodyTruncated: false,
    responseBodyTruncated: false,
    isBinary: false,
    error: null,
  };
}
